import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import AnalyticsTab from '../../components/social/AnalyticsTab';
import PublishedCalendarTab from '../../components/social/PublishedCalendarTab';
import PerformanceInsightsPanel from '../../components/social/PerformanceInsightsPanel';
import AICommandCenter from '../../components/social/AICommandCenter';
import { API } from '../../utils/config';
import { compressImage, postJsonWithProgress } from '../../utils/imageCompress';
import {
  Share2, Plus, Check, X, RefreshCw, Eye, Edit2, Send,
  Globe, MapPin, Clock, Trash2, ChevronDown, ChevronUp,
  Loader2, Image as ImageIcon, Copy, Settings, BarChart3,
  Target, ThumbsUp, MessageCircle, TrendingUp, Users, Zap,
  BookOpen, CheckCheck, XCircle, Brain, AlertCircle, Tag, AtSign, Handshake, Shield,
  LineChart, Upload, Search, Library, Sparkles, Video, PenTool,
  Languages,
  CalendarDays, Link as LinkIcon
} from 'lucide-react';


const VERTICALS = [
  { value: 'tourism', label: 'Turismo', color: 'bg-sky-100 text-sky-800' },
  { value: 'food', label: 'Enogastronomia', color: 'bg-orange-100 text-orange-800' },
  { value: 'culture', label: 'Cultura', color: 'bg-violet-100 text-violet-800' },
  { value: 'news', label: 'News Turismo', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'regulations', label: 'Normative', color: 'bg-rose-100 text-rose-800' },
  { value: 'events', label: 'Eventi', color: 'bg-amber-100 text-amber-800' },
];

const OBJECTIVES = [
  { value: 'likes', label: 'Like', icon: ThumbsUp, color: 'bg-blue-100 text-blue-800' },
  { value: 'engagement', label: 'Engagement', icon: MessageCircle, color: 'bg-purple-100 text-purple-800' },
  { value: 'leads', label: 'Lead', icon: Users, color: 'bg-green-100 text-green-800' },
  { value: 'traffic', label: 'Traffico', icon: TrendingUp, color: 'bg-cyan-100 text-cyan-800' },
  { value: 'brand_awareness', label: 'Brand', icon: Zap, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'bookings', label: 'Prenotazioni', icon: BookOpen, color: 'bg-red-100 text-red-800' },
];

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  published: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  failed: 'bg-red-200 text-red-900',
};

const STATUS_LABELS = {
  draft: 'Bozza',
  pending_approval: 'Da approvare',
  approved: 'Approvato',
  published: 'Pubblicato',
  rejected: 'Rifiutato',
  failed: 'Errore',
};

const PILLARS = [
  { value: 'awareness', label: 'Awareness', color: 'bg-sky-100 text-sky-800', desc: 'Educational/Ispirazionale' },
  { value: 'engagement', label: 'Engagement', color: 'bg-violet-100 text-violet-800', desc: 'Community & Networking' },
  { value: 'conversion', label: 'Conversion', color: 'bg-rose-100 text-rose-800', desc: 'Promozionale' },
  { value: 'authority', label: 'Authority', color: 'bg-emerald-100 text-emerald-800', desc: 'News & Trust' },
];

function getToken() { return localStorage.getItem('hw_token'); }
function apiHeaders() { return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }; }

// ============================================================
// PAGE FORM
// ============================================================
function PageForm({ page, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: page?.name || '', page_type: page?.page_type || 'regional',
    geo_area: page?.geo_area || '', language: page?.language || 'it',
    content_verticals: page?.content_verticals || ['tourism', 'food', 'culture', 'news'],
    post_objectives: page?.post_objectives || ['engagement', 'traffic'],
    content_pillars: page?.content_pillars || { awareness: 40, engagement: 30, conversion: 20, authority: 10 },
    lead_magnets: (page?.lead_magnets || []).join('\n'),
    auto_post: page?.auto_post || false,
    website_url: page?.website_url || 'https://www.holidayworld.it',
    fb_page_id: page?.fb_page_id || '',
    hashtags_base: (page?.hashtags_base || []).join(', ')
  });

  const handleSave = async () => {
    const payload = {
      ...form,
      hashtags_base: form.hashtags_base ? form.hashtags_base.split(',').map(h => h.trim()) : [],
      lead_magnets: form.lead_magnets ? form.lead_magnets.split('\n').map(l => l.trim()).filter(Boolean) : []
    };
    try {
      if (page) {
        await fetch(`${API}/api/social/pages/${page.id}`, { method: 'PUT', headers: apiHeaders(), body: JSON.stringify(payload) });
        toast.success('Pagina aggiornata');
      } else {
        await fetch(`${API}/api/social/pages`, { method: 'POST', headers: apiHeaders(), body: JSON.stringify(payload) });
        toast.success('Pagina creata');
      }
      onSave();
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const toggleItem = (key, val) => {
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(val) ? prev[key].filter(x => x !== val) : [...prev[key], val]
    }));
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="page-form">
      <CardContent className="pt-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input placeholder="Nome pagina" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="page-name-input" />
          <Input placeholder="Area geo (es. Liguria)" value={form.geo_area} onChange={e => setForm({...form, geo_area: e.target.value})} data-testid="page-geo-input" />
          <select className="border rounded-md px-3 py-2 text-sm bg-white" value={form.page_type} onChange={e => setForm({...form, page_type: e.target.value})} data-testid="page-type-select">
            <option value="institutional">Istituzionale</option>
            <option value="regional">Regionale</option>
            <option value="country">Nazione</option>
          </select>
          <select className="border rounded-md px-3 py-2 text-sm bg-white" value={form.language} onChange={e => setForm({...form, language: e.target.value})} data-testid="page-lang-select">
            <option value="it">Italiano</option><option value="en">English</option>
            <option value="es">Espanol</option><option value="fr">Francais</option>
            <option value="de">Deutsch</option><option value="nl">Nederlands</option>
            <option value="pt">Portugues</option><option value="pl">Polski</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Facebook Page ID" value={form.fb_page_id} onChange={e => setForm({...form, fb_page_id: e.target.value})} />
          <Input placeholder="Hashtag base (separati da virgola)" value={form.hashtags_base} onChange={e => setForm({...form, hashtags_base: e.target.value})} />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Verticali di contenuto</label>
          <div className="flex flex-wrap gap-1.5">
            {VERTICALS.map(v => (
              <Badge key={v.value} className={`cursor-pointer transition-all ${form.content_verticals.includes(v.value) ? v.color : 'bg-gray-100 text-gray-400 opacity-60'}`}
                onClick={() => toggleItem('content_verticals', v.value)}>
                {v.label}
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Obiettivi dei post</label>
          <div className="flex flex-wrap gap-1.5">
            {OBJECTIVES.map(o => {
              const Icon = o.icon;
              return (
                <Badge key={o.value} className={`cursor-pointer transition-all flex items-center gap-1 ${form.post_objectives.includes(o.value) ? o.color : 'bg-gray-100 text-gray-400 opacity-60'}`}
                  onClick={() => toggleItem('post_objectives', o.value)} data-testid={`obj-${o.value}`}>
                  <Icon className="h-3 w-3" /> {o.label}
                </Badge>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Pilastri Editoriali (%)</label>
          <div className="grid grid-cols-4 gap-2">
            {PILLARS.map(p => (
              <div key={p.value} className="text-center">
                <div className={`rounded-md px-2 py-1 text-[10px] font-semibold ${p.color} mb-1`}>{p.label}</div>
                <input type="number" min="0" max="100" className="w-full px-2 py-1 text-xs text-center border rounded"
                  value={form.content_pillars[p.value] || 0}
                  onChange={e => setForm({...form, content_pillars: {...form.content_pillars, [p.value]: parseInt(e.target.value) || 0}})}
                  data-testid={`pillar-${p.value}`} />
                <div className="text-[9px] text-slate-400">{p.desc}</div>
              </div>
            ))}
          </div>
          {Object.values(form.content_pillars).reduce((a,b) => a+b, 0) !== 100 && (
            <p className="text-[10px] text-rose-500 mt-1">Totale: {Object.values(form.content_pillars).reduce((a,b) => a+b, 0)}% (deve essere 100%)</p>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Lead Magnet (uno per riga)</label>
          <textarea className="w-full text-xs border rounded px-2 py-1 min-h-[40px]"
            placeholder="Es: Scarica la guida gratuita dei 5 posti segreti della Toscana&#10;Checklist per affittare la tua casa vacanza"
            value={form.lead_magnets} onChange={e => setForm({...form, lead_magnets: e.target.value})} />
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="auto-post-toggle">
            <div className={`relative w-10 h-5 rounded-full transition-colors ${form.auto_post ? 'bg-blue-600' : 'bg-gray-300'}`}
              onClick={() => setForm({...form, auto_post: !form.auto_post})}>
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.auto_post ? 'translate-x-5' : ''}`} />
            </div>
            <span className={form.auto_post ? 'text-blue-700 font-medium' : 'text-slate-500'}>Auto-Post (salta approvazione)</span>
          </label>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onCancel}>Annulla</Button>
          <Button size="sm" onClick={handleSave} data-testid="save-page-btn">{page ? 'Aggiorna' : 'Crea'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// PAGES TAB
// ============================================================
function PagesTab({ pages, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [userToken, setUserToken] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa pagina?')) return;
    await fetch(`${API}/api/social/pages/${id}`, { method: 'DELETE', headers: apiHeaders() });
    toast.success('Pagina eliminata');
    onRefresh();
  };

  const handleHealthCheck = async () => {
    setHealthCheckLoading(true);
    try {
      const r = await fetch(`${API}/api/social/facebook/health-check`, { method: 'POST', headers: apiHeaders(), body: JSON.stringify({ send_email: true }) });
      const data = await r.json();
      if (data.failed && data.failed.length > 0) {
        toast.error(`${data.failed.length} token in scadenza! Email inviata.`);
      } else {
        toast.success(`Tutti i ${data.valid} token sono validi. Email report inviata.`);
      }
    } catch (e) { toast.error('Errore verifica'); }
    setHealthCheckLoading(false);
  };

  const handleRefreshTokens = async () => {
    if (!userToken.trim()) { toast.error('Incolla il User Token'); return; }
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/api/social/facebook/refresh-all-tokens`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ user_token: userToken.trim() })
      });
      const data = await r.json();
      if (r.ok && data.success) {
        toast.success(`${data.updated} token aggiornati con token permanenti!`);
        setShowRefreshDialog(false);
        setUserToken('');
        onRefresh();
      } else {
        toast.error(data.detail || data.error || 'Errore nel refresh');
      }
    } catch (e) { toast.error('Errore di rete'); }
    setRefreshing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="text-base font-semibold text-slate-800">Pagine Social ({pages.length})</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowRefreshDialog(!showRefreshDialog)} data-testid="fb-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Rinnova Token
          </Button>
          <Button variant="outline" size="sm" onClick={handleHealthCheck} disabled={healthCheckLoading} data-testid="fb-health-check-btn">
            {healthCheckLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Shield className="h-4 w-4 mr-1" />}
            Verifica Token
          </Button>
          <Button onClick={() => { setEditingPage(null); setShowForm(true); }} size="sm" data-testid="add-page-btn">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi Pagina
          </Button>
        </div>
      </div>

      {showRefreshDialog && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="refresh-token-dialog">
          <CardContent className="pt-4 space-y-3">
            <div className="text-sm text-slate-700">
              <strong>Rinnova tutti i token Facebook</strong>
              <p className="text-xs text-slate-500 mt-1">
                1. Vai su <a href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noreferrer" className="text-blue-600 underline">Graph API Explorer</a><br/>
                2. Seleziona l'app "Holiday World Social Manager"<br/>
                3. Clicca "Generate Access Token" e accetta i permessi<br/>
                4. Copia il <strong>User Token</strong> e incollalo qui sotto
              </p>
            </div>
            <textarea className="w-full text-xs border rounded-md px-3 py-2 min-h-[60px] font-mono focus:ring-1 focus:ring-blue-400 focus:outline-none"
              placeholder="Incolla qui il User Token dal Graph API Explorer..."
              value={userToken} onChange={e => setUserToken(e.target.value)}
              data-testid="user-token-input" />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowRefreshDialog(false); setUserToken(''); }}>Annulla</Button>
              <Button size="sm" onClick={handleRefreshTokens} disabled={refreshing || !userToken.trim()} data-testid="confirm-refresh-btn">
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Rinnova tutti i token
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <PageForm
          page={editingPage}
          onSave={() => { setShowForm(false); setEditingPage(null); onRefresh(); }}
          onCancel={() => { setShowForm(false); setEditingPage(null); }}
        />
      )}

      <div className="grid gap-2">
        {pages.map(page => (
          <Card key={page.id} className="hover:shadow-md transition-shadow" data-testid={`page-card-${page.id}`}>
            <CardContent className="py-3 flex items-center gap-3">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${page.is_active ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Globe className={`h-4 w-4 ${page.is_active ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-slate-800">{page.name}</span>
                  <Badge className={page.page_type === 'institutional' ? 'bg-indigo-100 text-indigo-800' : page.page_type === 'regional' ? 'bg-emerald-100 text-emerald-800' : 'bg-cyan-100 text-cyan-800'}>
                    {page.page_type}
                  </Badge>
                  {page.auto_post && <Badge className="bg-amber-100 text-amber-800">Auto-Post</Badge>}
                  {page.fb_connected ? (
                    <Badge className="bg-green-100 text-green-800" data-testid={`fb-connected-${page.id}`}>
                      FB: {page.fb_page_name || 'Collegata'}
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500" data-testid={`fb-disconnected-${page.id}`}>FB Non collegata</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                  <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{page.geo_area}</span>
                  <span className="flex items-center gap-0.5"><Globe className="h-3 w-3" />{page.language?.toUpperCase()}</span>
                  <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{page.optimal_post_hour}:00</span>
                </div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {(page.post_objectives || []).map(o => {
                  const oInfo = OBJECTIVES.find(x => x.value === o);
                  return oInfo ? <Badge key={o} className={`text-[10px] ${oInfo.color}`}><oInfo.icon className="h-2.5 w-2.5 mr-0.5" />{oInfo.label}</Badge> : null;
                })}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => { setEditingPage(page); setShowForm(true); }} data-testid={`edit-page-${page.id}`}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(page.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {pages.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            Nessuna pagina configurata. Clicca "Aggiungi Pagina" per iniziare.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// POST CARD
// ============================================================
function PostCard({ post, pages, onRefresh, selected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState(post.edited_text || post.original_text);
  const [crossPostIds, setCrossPostIds] = useState(post.cross_post_page_ids || []);
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageTab, setImageTab] = useState('library');
  const [libraryImages, setLibraryImages] = useState([]);
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pexelsQuery, setPexelsQuery] = useState(post.geo_area || '');
  const [searchingPexels, setSearchingPexels] = useState(false);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [addingExtra, setAddingExtra] = useState(false);
  // === Traduzione italiana (solo revisione interna, non pubblicata) ===
  // Panel aperto di default per post stranieri cosi' da mostrare subito la traduzione
  const isNonItalian = post.language && post.language.toLowerCase() !== 'it';
  const [showTranslation, setShowTranslation] = useState(!!isNonItalian);
  const [translation, setTranslation] = useState(post.italian_translation || '');
  const [translating, setTranslating] = useState(false);

  const handleTranslateToItalian = async () => {
    if (!isNonItalian) return;
    setTranslating(true);
    try {
      const r = await fetch(`${API}/api/social/posts/${post.id}/translate-to-italian`, {
        method: 'POST',
        headers: apiHeaders(),
      });
      const data = await r.json();
      if (r.ok && data.italian_translation) {
        setTranslation(data.italian_translation);
        setShowTranslation(true);
        toast.success('Traduzione generata');
      } else if (data.already_italian) {
        toast.info('Il post e\' gia\' in italiano');
      } else {
        toast.error(data.detail || 'Errore durante la traduzione');
      }
    } catch {
      toast.error('Errore di rete durante la traduzione');
    }
    setTranslating(false);
  };

  const fetchLibraryImages = async () => {
    setLoadingLibrary(true);
    try {
      const r = await fetch(`${API}/api/social/image-library?geo_area=${encodeURIComponent(post.geo_area || '')}&limit=20`, { headers: apiHeaders() });
      const data = await r.json();
      setLibraryImages(data.images || []);
    } catch {}
    setLoadingLibrary(false);
  };

  const searchPexels = async () => {
    if (!pexelsQuery.trim()) return;
    setSearchingPexels(true);
    try {
      const r = await fetch(`${API}/api/social/image-library/search-pexels`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ query: pexelsQuery, geo_area: post.geo_area })
      });
      const data = await r.json();
      if (data.error) toast.error(data.error);
      setPexelsResults(data.photos || []);
    } catch { toast.error('Errore ricerca Pexels'); }
    setSearchingPexels(false);
  };

  const selectImage = async (imageUrl, source, photographer = '') => {
    setLoading(true);
    try {
      const endpoint = addingExtra
        ? `${API}/api/social/posts/${post.id}/add-image`
        : `${API}/api/social/posts/${post.id}/change-image`;
      const body = addingExtra
        ? { image_url: imageUrl, source, photographer }
        : { image_url: imageUrl, source, photographer, save_to_library: true };

      const r = await fetch(endpoint, {
        method: 'POST', headers: apiHeaders(), body: JSON.stringify(body)
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Errore (HTTP ${r.status})`);
        return;
      }

      const data = await r.json();
      if (data.success) {
        toast.success(addingExtra ? (data.message || 'Foto aggiunta') : 'Immagine aggiornata');
        if (!addingExtra) setShowImageModal(false);
        onRefresh();
      } else {
        toast.error(data.detail || 'Il server non ha confermato il salvataggio');
      }
    } catch {
      toast.error('Errore di rete');
    } finally {
      setLoading(false);
    }
  };

  const [uploadProgress, setUploadProgress] = useState(0);
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset input subito così se si riseleziona lo stesso file il change event scatta
    e.target.value = '';

    // Validazione formato (solo immagini comuni)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      toast.error(`Formato non supportato: ${file.type || 'sconosciuto'}. Usa JPG, PNG, WEBP o GIF.`);
      return;
    }

    // Validazione dimensione (max 25 MB ora che comprimiamo lato client)
    const MAX_SIZE = 25 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error(`File troppo grande: ${(file.size / 1024 / 1024).toFixed(1)} MB. Massimo 25 MB.`);
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    const t0 = Date.now();
    try {
      // GIF: niente compressione (perderebbe l'animazione)
      let payload;
      let info;
      if (file.type === 'image/gif') {
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        payload = base64;
        info = { originalSize: file.size, compressedSize: file.size };
      } else {
        const result = await compressImage(file, { maxSide: 2048, quality: 0.85 });
        payload = result.base64;
        info = result;
      }

      const savedKb = ((info.originalSize - info.compressedSize) / 1024).toFixed(0);
      if (info.compressedSize < info.originalSize) {
        toast.info(`Compressa: ${(info.originalSize / 1024 / 1024).toFixed(1)} MB → ${(info.compressedSize / 1024 / 1024).toFixed(1)} MB (-${savedKb} KB). Caricamento in corso...`);
      }

      const endpoint = addingExtra
        ? `${API}/api/social/posts/${post.id}/add-image`
        : `${API}/api/social/posts/${post.id}/change-image`;
      const body = addingExtra
        ? { image_base64: payload, source: 'upload' }
        : { image_base64: payload, source: 'upload', save_to_library: true };

      const result = await postJsonWithProgress(
        endpoint,
        body,
        apiHeaders(),
        (percent) => setUploadProgress(percent)
      );

      if (!result.ok) {
        const detail = result.data?.detail || `Errore upload (HTTP ${result.status})`;
        toast.error(detail);
        return;
      }

      if (result.data?.success) {
        const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
        toast.success(`${addingExtra ? 'Foto aggiunta' : 'Foto caricata'} in ${elapsed}s`);
        if (!addingExtra) setShowImageModal(false);
        onRefresh();
      } else {
        toast.error(result.data?.detail || 'Errore: il server non ha confermato il salvataggio');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Errore durante la compressione/upload del file');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const updatePayload = { cross_post_page_ids: crossPostIds };
      const hasEdits = editing && editedText !== post.original_text;
      if (hasEdits) updatePayload.edited_text = editedText;

      // Step 1: salva eventuali edit + cross-post
      const r1 = await fetch(`${API}/api/social/posts/${post.id}`, {
        method: 'PUT', headers: apiHeaders(), body: JSON.stringify(updatePayload)
      });
      if (!r1.ok) {
        const err = await r1.json().catch(() => ({}));
        toast.error(err.detail || `Errore salvataggio modifiche (HTTP ${r1.status})`);
        setLoading(false);
        return;
      }

      // Step 2: approva
      const r2 = await fetch(`${API}/api/social/posts/${post.id}/approve`, {
        method: 'POST', headers: apiHeaders()
      });
      if (!r2.ok) {
        const err = await r2.json().catch(() => ({}));
        toast.error(err.detail || `Errore approvazione post (HTTP ${r2.status})`);
        setLoading(false);
        return;
      }

      toast.success('Post approvato');
      if (hasEdits) toast.info('L\'AI ha appreso dalle tue modifiche', { duration: 4000 });
      setEditing(false);
      onRefresh();
    } catch (e) {
      toast.error('Errore di rete durante l\'approvazione');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Motivo del rifiuto:');
    if (reason === null) return;
    await fetch(`${API}/api/social/posts/${post.id}/reject`, { method: 'POST', headers: apiHeaders(), body: JSON.stringify({ reason }) });
    toast.success('Post rifiutato'); onRefresh();
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    await fetch(`${API}/api/social/posts/${post.id}`, { method: 'PUT', headers: apiHeaders(), body: JSON.stringify({ status: newStatus }) });
    toast.success(newStatus === 'pending_approval' ? 'Post riportato in attesa' : 'Stato aggiornato'); setLoading(false); onRefresh();
  };

  const handleRegenerate = async () => {
    setLoading(true);
    await fetch(`${API}/api/social/posts/${post.id}/generate-single`, { method: 'POST', headers: apiHeaders() });
    toast.success('Post rigenerato'); setLoading(false); onRefresh();
  };

  const handlePublish = async () => {
    if (!window.confirm('Pubblicare questo post su Facebook?')) return;
    setLoading(true);
    // Feedback immediato: il tempo di risposta di Facebook Graph API
    // puo' essere 5-30 secondi, l'utente deve sapere che il sistema sta lavorando
    const toastId = toast.loading('Pubblicazione su Facebook in corso...');
    try {
      const r = await fetch(`${API}/api/social/posts/${post.id}/publish`, {
        method: 'POST', headers: apiHeaders()
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Errore pubblicazione (HTTP ${r.status})`, { id: toastId });
        return;
      }

      const data = await r.json().catch(() => null);
      if (!data) {
        toast.error('Risposta backend non valida', { id: toastId });
        return;
      }
      if (data.success) {
        toast.success('Post pubblicato su Facebook!', { id: toastId });
      } else {
        toast.error(data.error || data.detail || 'Errore Facebook', { id: toastId });
      }
    } catch (e) {
      toast.error('Errore di rete durante la pubblicazione', { id: toastId });
    } finally {
      setLoading(false);
      onRefresh();
    }
  };

  const toggleCrossPost = (pageId) => {
    setCrossPostIds(prev => prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]);
  };

  const verticalInfo = VERTICALS.find(v => v.value === post.content_vertical);
  const objectiveInfo = OBJECTIVES.find(o => o.value === post.post_objective);

  return (
    <Card className={`transition-all ${selected ? 'ring-2 ring-blue-400' : ''} ${post.status === 'rejected' ? 'border-rose-200 bg-rose-50/30' : post.status === 'published' ? 'border-emerald-200 bg-emerald-50/20' : ''}`}
      data-testid={`post-card-${post.id}`}>
      <CardContent className="py-3">
        <div className="flex items-start gap-3">
          {/* Select checkbox for batch */}
          {(post.status === 'pending_approval' || post.status === 'approved') && onToggleSelect && (
            <label className="flex items-center pt-1 cursor-pointer" data-testid={`select-post-${post.id}`}>
              <input type="checkbox" checked={selected} onChange={() => onToggleSelect(post.id)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600" />
            </label>
          )}

          {/* Image */}
          <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100 relative">
            {post.image_url ? (
              <img src={post.image_url} alt="" className="w-full h-full object-cover" />
            ) : post.image_base64 ? (
              <img src={post.image_base64.startsWith('data:') ? post.image_base64 : `data:image/png;base64,${post.image_base64}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-6 w-6 text-gray-300" /></div>
            )}
            {post.image_source && post.image_source.startsWith('pexels') && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5 leading-none">Pexels</div>
            )}
            {(post.additional_images || []).length > 0 && (
              <div className="absolute top-0 right-0 bg-blue-600 text-white text-[9px] font-bold px-1 py-0.5 rounded-bl leading-none">{(post.additional_images || []).length + 1}</div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span className="font-semibold text-sm text-slate-800">{post.page_name}</span>
              <Badge className={STATUS_COLORS[post.status]}>{STATUS_LABELS[post.status]}</Badge>
              {verticalInfo && <Badge className={verticalInfo.color}>{verticalInfo.label}</Badge>}
              {objectiveInfo && (
                <Badge className={objectiveInfo.color}>
                  <objectiveInfo.icon className="h-3 w-3 mr-0.5" />{objectiveInfo.label}
                </Badge>
              )}
              {(() => { const pi = PILLARS.find(p => p.value === post.content_pillar); return pi ? <Badge className={pi.color}>{pi.label}</Badge> : null; })()}
              <span className="text-xs text-slate-400 ml-auto">{post.scheduled_date} {post.scheduled_time}</span>
            </div>
            {!editing ? (
              <p className="text-sm text-slate-600 line-clamp-2">{post.edited_text || post.original_text}</p>
            ) : (
              <textarea className="w-full text-sm border rounded-md p-2 min-h-[120px] focus:ring-1 focus:ring-blue-400 focus:outline-none"
                value={editedText} onChange={e => setEditedText(e.target.value)} data-testid={`edit-post-text-${post.id}`} />
            )}
            {post.source_summary && <p className="text-xs text-slate-400 mt-0.5">Fonte: {post.source_summary}</p>}
            {post.tags_used?.length > 0 && (
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                <AtSign className="h-3 w-3 text-indigo-500" />
                {post.tags_used.map((t, i) => (
                  <Badge key={i} className="bg-indigo-100 text-indigo-700 text-[10px]">{t.handle || t.name}</Badge>
                ))}
              </div>
            )}
            {post.rejection_reason && <p className="text-xs text-rose-500 mt-0.5">Rifiutato: {post.rejection_reason}</p>}
            {post.fb_post_id && <p className="text-xs text-green-600 mt-0.5">FB Post ID: {post.fb_post_id}</p>}
            {post.fb_error && <p className="text-xs text-rose-500 mt-0.5">FB Errore: {post.fb_error}</p>}
          </div>

          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="flex-shrink-0">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t space-y-3">
            {/* Nota strategica di Holly */}
            {post.strategic_note && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-3">
                <label className="text-xs font-semibold text-blue-700 flex items-center gap-1 mb-1">
                  <Brain className="h-3.5 w-3.5" /> Holly - Nota Strategica:
                </label>
                <p className="text-sm text-blue-900 italic">{post.strategic_note}</p>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-500">Testo completo:</label>
              {!editing ? (
                <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1 bg-white rounded-md p-3 border">{post.edited_text || post.original_text}</p>
              ) : (
                <textarea className="w-full text-sm border rounded-md p-3 min-h-[200px] mt-1 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  value={editedText} onChange={e => setEditedText(e.target.value)} />
              )}
            </div>

            {/* === TRADUZIONE ITALIANA (solo revisione, non pubblicata) === */}
            {isNonItalian && (
              <div className="border border-dashed border-indigo-300 rounded-md bg-indigo-50/40" data-testid="italian-translation-section">
                <button
                  type="button"
                  onClick={() => setShowTranslation(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-indigo-50 rounded-md"
                  data-testid="toggle-italian-translation"
                >
                  <span className="flex items-center gap-2 text-xs font-semibold text-indigo-800">
                    <Languages className="h-3.5 w-3.5" />
                    Traduzione in italiano (solo per revisione, NON viene pubblicata)
                    <Badge className="bg-indigo-100 text-indigo-700 text-[10px] uppercase ml-1">
                      {(post.language || '').toUpperCase()} → IT
                    </Badge>
                  </span>
                  {showTranslation ? <ChevronUp className="h-4 w-4 text-indigo-600" /> : <ChevronDown className="h-4 w-4 text-indigo-600" />}
                </button>
                {showTranslation && (
                  <div className="px-3 pb-3">
                    {translation ? (
                      <>
                        <p
                          className="text-sm text-slate-800 whitespace-pre-wrap bg-white rounded-md p-3 border border-indigo-200 italic"
                          data-testid="italian-translation-text"
                        >
                          {translation}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-[11px] text-indigo-700/80">
                            Traduzione automatica (Gemini). Usala per capire il contenuto prima di approvare.
                            Il post pubblicato sara' nella lingua originale.
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-indigo-700"
                            disabled={translating}
                            onClick={handleTranslateToItalian}
                            data-testid="regenerate-italian-translation"
                          >
                            {translating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                            Rigenera
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-start gap-2 py-2">
                        <p className="text-xs text-slate-600 flex items-center gap-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />
                          Traduzione automatica in corso in background. Riapri la pagina tra qualche secondo oppure forza ora:
                        </p>
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          disabled={translating}
                          onClick={handleTranslateToItalian}
                          data-testid="generate-italian-translation"
                        >
                          {translating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Languages className="h-3.5 w-3.5 mr-1" />}
                          {translating ? 'Traduzione in corso...' : 'Genera ora'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Image section with change button */}
            <div>
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                Immagine:
                {post.image_source && post.image_source.startsWith('pexels') && (
                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Foto reale (Pexels)</span>
                )}
                {post.image_source && post.image_source.startsWith('library') && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">Libreria</span>
                )}
                {post.image_source === 'ai' && (
                  <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">Generata AI</span>
                )}
                {post.image_source === 'upload' && (
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Caricata</span>
                )}
                <Button
                  variant="ghost" size="sm"
                  className="ml-auto h-6 text-[10px] text-blue-600 hover:text-blue-800"
                  onClick={() => { setShowImageModal(true); fetchLibraryImages(); }}
                  data-testid="change-image-btn"
                >
                  <ImageIcon className="h-3 w-3 mr-1" /> Cambia immagine
                </Button>
              </label>
              {post.image_url ? (
                <img src={post.image_url} alt="" className="mt-1 rounded-lg max-h-64 object-cover cursor-pointer" onClick={() => { setShowImageModal(true); fetchLibraryImages(); }} />
              ) : post.image_base64 ? (
                <img src={post.image_base64.startsWith('data:') ? post.image_base64 : `data:image/png;base64,${post.image_base64}`} alt="" className="mt-1 rounded-lg max-h-64 object-cover cursor-pointer" onClick={() => { setShowImageModal(true); fetchLibraryImages(); }} />
              ) : (
                <div className="mt-1 rounded-lg h-32 bg-slate-100 flex items-center justify-center cursor-pointer border-2 border-dashed border-slate-300" onClick={() => { setShowImageModal(true); fetchLibraryImages(); }}>
                  <div className="text-center text-slate-400">
                    <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                    <span className="text-xs">Clicca per aggiungere</span>
                  </div>
                </div>
              )}
            </div>

            {/* Video player if present */}
            {post.video_url && (
              <div className="mt-2 rounded-lg overflow-hidden border">
                <video src={post.video_url.startsWith('/api') ? `${API}${post.video_url}` : post.video_url} className="w-full max-h-[300px]" controls data-testid="post-video" />
                <div className="bg-slate-50 px-2 py-1 text-[10px] text-slate-500 flex items-center gap-1"><Video className="h-3 w-3" /> Video allegato</div>
              </div>
            )}

            {/* Additional Images Gallery + Add button */}
            <div className="mt-2">
              {(post.additional_images || []).length > 0 && (
                <div className="flex gap-2 flex-wrap mt-1">
                  {(post.additional_images || []).map((img, idx) => (
                    <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden border">
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={async () => {
                          try {
                            const r = await fetch(`${API}/api/social/posts/${post.id}/remove-image/${idx}`, { method: 'DELETE', headers: apiHeaders() });
                            const d = await r.json();
                            if (d.success) { toast.success('Foto rimossa'); onRefresh(); }
                          } catch { toast.error('Errore'); }
                        }}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Rimuovi"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {img.source && img.source.startsWith('pexels') && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[7px] text-center py-0.5">Pexels</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <label className="cursor-pointer" data-testid="add-photo-upload">
                  <input type="file" className="hidden" accept="image/*" multiple onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    for (const file of files) {
                      const reader = new FileReader();
                      reader.onload = async () => {
                        try {
                          const r = await fetch(`${API}/api/social/posts/${post.id}/add-image`, {
                            method: 'POST', headers: apiHeaders(),
                            body: JSON.stringify({ image_base64: reader.result, source: 'upload' })
                          });
                          const d = await r.json();
                          if (d.success) toast.success(d.message);
                          else toast.error(d.detail || 'Errore');
                        } catch { toast.error('Errore upload'); }
                        onRefresh();
                      };
                      reader.readAsDataURL(file);
                    }
                    e.target.value = '';
                  }} />
                  <span className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 border border-dashed border-blue-300 rounded-lg px-2.5 py-1.5 hover:bg-blue-50 transition-colors">
                    <Plus className="h-3 w-3 mr-1" /> Aggiungi foto
                  </span>
                </label>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {(post.additional_images || []).length + (post.image_url || post.image_base64 ? 1 : 0)} foto totali
              </p>
            </div>

            {/* Image Change Modal */}
            {showImageModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowImageModal(false)}>
                <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                  <div className="p-4 border-b flex items-center justify-between">
                    <h3 className="font-bold text-slate-800">Gestisci immagini - {post.page_name}</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowImageModal(false)}><X className="h-4 w-4" /></Button>
                  </div>

                  {/* Mode toggle: replace main vs add additional */}
                  <div className="px-4 pt-3 flex gap-2">
                    <button
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!addingExtra ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      onClick={() => setAddingExtra(false)}
                    >Cambia immagine principale</button>
                    <button
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${addingExtra ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      onClick={() => setAddingExtra(true)}
                    >Aggiungi foto extra</button>
                  </div>
                  
                  {/* Tabs */}
                  <div className="flex border-b">
                    {[
                      { id: 'library', label: 'Libreria', icon: Library },
                      { id: 'pexels', label: 'Cerca Pexels', icon: Search },
                      { id: 'upload', label: 'Carica', icon: Upload }
                    ].map(tab => (
                      <button key={tab.id}
                        className={`flex-1 py-2.5 px-3 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
                          imageTab === tab.id ? 'border-blue-500 text-blue-600 bg-blue-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                        onClick={() => setImageTab(tab.id)}
                      >
                        <tab.icon className="h-3.5 w-3.5" /> {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="p-4 overflow-y-auto max-h-[55vh]">
                    {/* Library Tab */}
                    {imageTab === 'library' && (
                      <div>
                        {loadingLibrary ? (
                          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
                        ) : libraryImages.length === 0 ? (
                          <p className="text-center text-slate-400 py-8 text-sm">Nessuna immagine in libreria per "{post.geo_area}". Cerca su Pexels o carica una foto.</p>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {libraryImages.map(img => (
                              <div key={img.id} className="relative group cursor-pointer rounded-lg overflow-hidden border hover:border-blue-400 transition-colors"
                                onClick={() => selectImage(img.url, 'library', img.photographer)} data-testid={`library-img-${img.id}`}>
                                <img src={img.url} alt="" className="w-full h-24 object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                  <Check className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] px-1 py-0.5 flex justify-between">
                                  <span>{img.source}</span>
                                  <span>{img.usage_count}x</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Pexels Search Tab */}
                    {imageTab === 'pexels' && (
                      <div>
                        <div className="flex gap-2 mb-3">
                          <Input
                            value={pexelsQuery}
                            onChange={e => setPexelsQuery(e.target.value)}
                            placeholder="es. Cinque Terre Liguria..."
                            onKeyDown={e => e.key === 'Enter' && searchPexels()}
                            className="text-sm"
                            data-testid="pexels-search-input"
                          />
                          <Button onClick={searchPexels} disabled={searchingPexels} size="sm" data-testid="pexels-search-btn">
                            {searchingPexels ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                          </Button>
                        </div>
                        {pexelsResults.length > 0 ? (
                          <div className="grid grid-cols-3 gap-2">
                            {pexelsResults.map(photo => (
                              <div key={photo.id} className="relative group cursor-pointer rounded-lg overflow-hidden border hover:border-blue-400 transition-colors"
                                onClick={() => selectImage(photo.url, 'pexels', photo.photographer)} data-testid={`pexels-img-${photo.id}`}>
                                <img src={photo.thumb} alt={photo.alt} className="w-full h-24 object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                  <Check className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] px-1 py-0.5">{photo.photographer}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-slate-400 py-6 text-sm">Cerca una località per trovare foto reali</p>
                        )}
                      </div>
                    )}

                    {/* Upload Tab */}
                    {imageTab === 'upload' && (
                      <div className="flex flex-col items-center justify-center py-8">
                        {!loading ? (
                          <label className="cursor-pointer flex flex-col items-center gap-3 p-8 border-2 border-dashed rounded-xl border-slate-300 hover:border-blue-400 transition-colors w-full" data-testid="upload-image-label">
                            <Upload className="h-10 w-10 text-slate-400" />
                            <span className="text-sm text-slate-600 font-medium">Clicca o trascina per caricare</span>
                            <span className="text-xs text-slate-400">JPG, PNG, WEBP, GIF - fino a 25 MB (compressione automatica)</span>
                            <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                          </label>
                        ) : (
                          <div className="w-full p-6 border-2 border-dashed border-blue-300 bg-blue-50/40 rounded-xl flex flex-col items-center gap-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                              <Loader2 className="h-5 w-5 animate-spin" />
                              {uploadProgress < 5 ? 'Compressione immagine...' : uploadProgress < 100 ? `Caricamento ${uploadProgress}%` : 'Salvataggio sul server...'}
                            </div>
                            <div className="w-full bg-blue-100 rounded-full h-3 overflow-hidden">
                              <div
                                className="h-3 bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-200 ease-out"
                                style={{ width: `${Math.max(uploadProgress, 5)}%` }}
                              />
                            </div>
                            <span className="text-[11px] text-slate-500">
                              {uploadProgress < 100 ? 'Non chiudere la finestra...' : 'Quasi fatto!'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Outreach message per cross-promotion */}
            {post.outreach_message && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
                <label className="text-xs font-semibold text-indigo-700 flex items-center gap-1 mb-1">
                  <Handshake className="h-3.5 w-3.5" /> Messaggio Cross-Promotion suggerito:
                </label>
                <p className="text-sm text-indigo-900">{post.outreach_message}</p>
                <Button variant="ghost" size="sm" className="mt-1 text-indigo-600 h-6 text-[10px]"
                  onClick={() => { navigator.clipboard.writeText(post.outreach_message); toast.success('Copiato!'); }}>
                  <Copy className="h-3 w-3 mr-1" /> Copia messaggio
                </Button>
              </div>
            )}

            {post.status === 'pending_approval' && (
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">
                  <Copy className="h-3 w-3 inline mr-1" />Cross-post su altre pagine:
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {pages.filter(p => p.id !== post.page_id).map(p => (
                    <Badge key={p.id}
                      className={`cursor-pointer transition-all ${crossPostIds.includes(p.id) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      onClick={() => toggleCrossPost(p.id)}>
                      {p.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2 flex-wrap">
              {post.status === 'pending_approval' && (
                <>
                  <Button size="sm" onClick={handleApprove} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-post-${post.id}`}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Approva
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} data-testid={`edit-post-${post.id}`}>
                    <Edit2 className="h-4 w-4 mr-1" /> {editing ? 'Annulla' : 'Modifica'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={loading}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Rigenera
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleReject} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                    <X className="h-4 w-4 mr-1" /> Rifiuta
                  </Button>
                </>
              )}
              {post.status === 'approved' && (
                <>
                  <Button size="sm" onClick={handlePublish} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid={`publish-post-${post.id}`}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Pubblica ora
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleStatusChange('pending_approval')} disabled={loading} data-testid={`unapprove-post-${post.id}`}>
                    <RefreshCw className="h-4 w-4 mr-1" /> In attesa
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleReject} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`reject-approved-post-${post.id}`}>
                    <X className="h-4 w-4 mr-1" /> Rifiuta
                  </Button>
                </>
              )}
              {post.status === 'published' && (
                <Button size="sm" onClick={handlePublish} disabled={loading} variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid={`republish-post-${post.id}`}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Ripubblica
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// APPROVAL TAB
// ============================================================
function ApprovalTab({ posts, pages, onRefresh }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);

  const pendingPosts = posts.filter(p => p.status === 'pending_approval');

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === pendingPosts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingPosts.map(p => p.id)));
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.size === 0) return;
    setBatchLoading(true);
    await fetch(`${API}/api/social/posts/batch-approve`, {
      method: 'POST', headers: apiHeaders(), body: JSON.stringify({ post_ids: [...selectedIds] })
    });
    toast.success(`${selectedIds.size} post approvati`);
    setSelectedIds(new Set()); setBatchLoading(false); onRefresh();
  };

  const handleBatchReject = async () => {
    if (selectedIds.size === 0) return;
    const reason = window.prompt('Motivo del rifiuto batch:');
    if (reason === null) return;
    setBatchLoading(true);
    await fetch(`${API}/api/social/posts/batch-reject`, {
      method: 'POST', headers: apiHeaders(), body: JSON.stringify({ post_ids: [...selectedIds], reason })
    });
    toast.success(`${selectedIds.size} post rifiutati`);
    setSelectedIds(new Set()); setBatchLoading(false); onRefresh();
  };

  if (pendingPosts.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400" data-testid="no-pending-posts">
        <CheckCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p className="text-base">Nessun post da approvare</p>
        <p className="text-sm mt-1">Tutti i post sono stati revisionati</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="approval-tab">
      {/* Batch controls */}
      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="select-all-posts">
          <input type="checkbox" checked={selectedIds.size === pendingPosts.length && pendingPosts.length > 0}
            onChange={selectAll} className="h-4 w-4 rounded border-gray-300" />
          <span className="font-medium text-amber-800">Seleziona tutti ({pendingPosts.length})</span>
        </label>
        {selectedIds.size > 0 && (
          <>
            <span className="text-xs text-amber-600">{selectedIds.size} selezionati</span>
            <div className="flex-1" />
            <Button size="sm" onClick={handleBatchApprove} disabled={batchLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="batch-approve-btn">
              {batchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1" />}
              Approva tutti
            </Button>
            <Button size="sm" variant="outline" onClick={handleBatchReject} disabled={batchLoading} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="batch-reject-btn">
              <XCircle className="h-4 w-4 mr-1" /> Rifiuta tutti
            </Button>
          </>
        )}
      </div>

      <div className="space-y-2">
        {pendingPosts.map(p => (
          <PostCard key={p.id} post={p} pages={pages} onRefresh={onRefresh}
            selected={selectedIds.has(p.id)} onToggleSelect={toggleSelect} />
        ))}
      </div>
    </div>
  );
}

// ============================================================
// APPROVED POSTS SECTION (with Batch Publish)
// ============================================================
function ApprovedPostsSection({ posts, pages, onRefresh }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === posts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(posts.map(p => p.id)));
    }
  };

  const handleBatchPublish = async () => {
    const ids = selectedIds.size > 0 ? [...selectedIds] : posts.map(p => p.id);
    const count = ids.length;
    if (!window.confirm(`Pubblicare ${count} post su Facebook?`)) return;
    
    setPublishing(true);
    setProgress({ current: 0, total: count });
    
    try {
      const r = await fetch(`${API}/api/social/posts/batch-publish`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ post_ids: ids })
      });
      const data = await r.json();
      
      if (data.published > 0) {
        toast.success(`${data.published} post pubblicati su Facebook`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} post falliti: ${data.errors?.map(e => e.page + ': ' + e.error).join(', ')}`);
      }
    } catch (err) {
      toast.error('Errore durante la pubblicazione batch');
    }
    
    setSelectedIds(new Set());
    setPublishing(false);
    setProgress({ current: 0, total: 0 });
    // Piccolo delay per dare tempo al backend di propagare l'update DB
    setTimeout(() => onRefresh(), 600);
  };

  return (
    <div data-testid="approved-posts-section">
      <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
        <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="select-all-approved">
          <input type="checkbox" checked={selectedIds.size === posts.length && posts.length > 0}
            onChange={selectAll} className="h-4 w-4 rounded border-gray-300" />
          <span className="font-medium text-blue-800">
            <Check className="h-4 w-4 inline mr-1" />Approvati ({posts.length})
          </span>
        </label>
        {selectedIds.size > 0 && (
          <span className="text-xs text-blue-600">{selectedIds.size} selezionati</span>
        )}
        <div className="flex-1" />
        <Button size="sm" onClick={handleBatchPublish} disabled={publishing}
          className="bg-blue-600 hover:bg-blue-700" data-testid="batch-publish-btn">
          {publishing ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Pubblicazione...</>
          ) : (
            <><Send className="h-4 w-4 mr-1" /> Pubblica {selectedIds.size > 0 ? `selezionati (${selectedIds.size})` : `tutti (${posts.length})`}</>
          )}
        </Button>
      </div>
      <div className="space-y-2">
        {posts.map(p => (
          <PostCard key={p.id} post={p} pages={pages} onRefresh={onRefresh}
            selected={selectedIds.has(p.id)} onToggleSelect={toggleSelect} />
        ))}
      </div>
    </div>
  );
}


// ============================================================
// LEARNING TAB (Phase 3)
// ============================================================
function LearningTab({ pages }) {
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualForm, setManualForm] = useState({ page_id: '', instruction: '', feedback_type: 'style' });
  const [showManualForm, setShowManualForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/social/feedback-summary`, { headers: apiHeaders() });
    if (r.ok) setFeedbackData(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Eliminare questo feedback? La regola AI verra rimossa.')) return;
    await fetch(`${API}/api/social/feedback/${feedbackId}`, { method: 'DELETE', headers: apiHeaders() });
    toast.success('Feedback eliminato');
    fetchFeedback();
  };

  const handleResetPage = async (pageId, pageName) => {
    if (!window.confirm(`Resettare TUTTE le regole AI apprese per "${pageName}"? L'AI ripartira da zero per questa pagina.`)) return;
    const r = await fetch(`${API}/api/social/feedback/reset/${pageId}`, { method: 'DELETE', headers: apiHeaders() });
    if (r.ok) {
      const data = await r.json();
      toast.success(`${data.deleted_count} regole eliminate per ${pageName}`);
      fetchFeedback();
    } else toast.error('Errore nel reset');
  };

  const handleAddManualRule = async () => {
    if (!manualForm.page_id || !manualForm.instruction.trim()) {
      toast.error('Seleziona una pagina e scrivi la regola');
      return;
    }
    setSaving(true);
    const r = await fetch(`${API}/api/social/feedback/manual`, {
      method: 'POST', headers: apiHeaders(),
      body: JSON.stringify(manualForm)
    });
    if (r.ok) {
      toast.success('Regola aggiunta');
      setManualForm({ page_id: '', instruction: '', feedback_type: 'style' });
      setShowManualForm(false);
      fetchFeedback();
    } else toast.error('Errore nel salvataggio');
    setSaving(false);
  };

  const FEEDBACK_TYPES = [
    { value: 'style', label: 'Stile', desc: 'Tono, voce, personalita' },
    { value: 'tone', label: 'Tono', desc: 'Formale/informale, serio/leggero' },
    { value: 'length', label: 'Lunghezza', desc: 'Piu corto o piu lungo' },
    { value: 'hashtag', label: 'Hashtag', desc: 'Quali hashtag usare o evitare' },
    { value: 'content', label: 'Contenuto', desc: 'Argomenti, temi, approccio' },
  ];

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  const totalRules = feedbackData.reduce((acc, p) => acc + p.feedback_count, 0);

  return (
    <div className="space-y-4" data-testid="learning-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Brain className="h-5 w-5 text-violet-600" />
        <div className="flex-1">
          <h3 className="text-base font-semibold text-slate-800">Apprendimento AI</h3>
          <p className="text-xs text-slate-500">
            L'AI impara dalle tue modifiche ai post e dalle regole manuali. Ogni correzione diventa una regola permanente.
          </p>
        </div>
        <Badge className="bg-violet-100 text-violet-800">{totalRules} regole totali</Badge>
        <Button size="sm" onClick={() => setShowManualForm(!showManualForm)} data-testid="add-manual-rule-btn">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi Regola
        </Button>
      </div>

      {/* Info banner about automatic learning */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-blue-800">
          <strong>Come funziona:</strong> Quando modifichi il testo di un post prima di approvarlo, l'AI analizza le differenze 
          e genera automaticamente una regola. Puoi anche aggiungere regole manuali qui sotto. Tutte le regole vengono 
          incluse nel prompt AI per i post futuri della pagina.
        </div>
      </div>

      {/* Manual rule form */}
      {showManualForm && (
        <Card className="border-violet-200 bg-violet-50/30" data-testid="manual-rule-form">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select className="border rounded-md px-3 py-2 text-sm bg-white"
                value={manualForm.page_id} onChange={e => setManualForm({...manualForm, page_id: e.target.value})}
                data-testid="manual-rule-page-select">
                <option value="">Seleziona pagina...</option>
                {pages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.geo_area})</option>)}
              </select>
              <select className="border rounded-md px-3 py-2 text-sm bg-white"
                value={manualForm.feedback_type} onChange={e => setManualForm({...manualForm, feedback_type: e.target.value})}
                data-testid="manual-rule-type-select">
                {FEEDBACK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} - {t.desc}</option>)}
              </select>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowManualForm(false)}>Annulla</Button>
                <Button size="sm" onClick={handleAddManualRule} disabled={saving} data-testid="save-manual-rule-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Salva
                </Button>
              </div>
            </div>
            <textarea className="w-full text-sm border rounded-md px-3 py-2 min-h-[80px] focus:ring-1 focus:ring-violet-400 focus:outline-none"
              placeholder="Scrivi la regola per l'AI. Es: 'Non usare mai il punto esclamativo alla fine del post' oppure 'Inizia sempre i post con una domanda retorica'"
              value={manualForm.instruction} onChange={e => setManualForm({...manualForm, instruction: e.target.value})}
              data-testid="manual-rule-instruction" />
          </CardContent>
        </Card>
      )}

      {feedbackData.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Nessun feedback ancora.</p>
          <p className="text-sm mt-1">Modifica un post o aggiungi una regola manuale per insegnare all'AI.</p>
        </div>
      ) : (
        feedbackData.map(page => (
          <Card key={page.page_id} data-testid={`feedback-page-${page.page_id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-600" /> {page.page_name}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className="bg-violet-100 text-violet-800">{page.feedback_count} correzioni</Badge>
                  <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-700 h-7 text-xs"
                    onClick={() => handleResetPage(page.page_id, page.page_name)}
                    data-testid={`reset-feedback-${page.page_id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Reset AI
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {page.style_notes && (
                <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
                  <label className="text-xs font-semibold text-violet-700 block mb-1">Regole AI apprese:</label>
                  <p className="text-sm text-violet-900 whitespace-pre-wrap">{page.style_notes}</p>
                </div>
              )}
              {page.recent_feedbacks.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-500">Ultime correzioni:</label>
                  {page.recent_feedbacks.map(fb => (
                    <div key={fb.id} className="flex items-start gap-2 bg-slate-50 rounded-md p-2 group" data-testid={`feedback-item-${fb.id}`}>
                      <div className={`h-5 w-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${fb.post_id === 'manual' ? 'bg-violet-100' : 'bg-blue-100'}`}>
                        {fb.post_id === 'manual' ? <Edit2 className="h-3 w-3 text-violet-600" /> : <Brain className="h-3 w-3 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Badge className={`text-[9px] px-1 ${fb.post_id === 'manual' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                            {fb.post_id === 'manual' ? 'Manuale' : 'Auto'}
                          </Badge>
                          <Badge className="text-[9px] px-1 bg-slate-100 text-slate-600">{fb.feedback_type}</Badge>
                        </div>
                        <p className="text-xs text-slate-700">{fb.instruction}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {fb.created_by} - {new Date(fb.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteFeedback(fb.id)}
                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-rose-500 h-8 w-8 p-0 touch-manipulation" data-testid={`delete-feedback-${fb.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// ============================================================
// TAGS TAB (Smart Tagging & Cross-Promotion)
// ============================================================
const ENTITY_TYPES = [
  { value: 'comune', label: 'Comune', color: 'bg-blue-100 text-blue-800' },
  { value: 'proloco', label: 'Pro Loco', color: 'bg-green-100 text-green-800' },
  { value: 'community', label: 'Community', color: 'bg-purple-100 text-purple-800' },
  { value: 'news', label: 'News Locale', color: 'bg-orange-100 text-orange-800' },
  { value: 'partner', label: 'Partner', color: 'bg-cyan-100 text-cyan-800' },
];

function TagsTab() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [filterRegion, setFilterRegion] = useState('');
  const [form, setForm] = useState({
    name: '', region: '', fb_handle: '', ig_handle: '',
    entity_type: 'community', keywords: '', priority: 2, collaboration_note: ''
  });

  const fetchTags = useCallback(async () => {
    setLoading(true);
    let url = `${API}/api/social/tags`;
    if (filterRegion) url += `?region=${filterRegion}`;
    const r = await fetch(url, { headers: apiHeaders() });
    if (r.ok) setTags(await r.json());
    setLoading(false);
  }, [filterRegion]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const resetForm = () => {
    setForm({ name: '', region: '', fb_handle: '', ig_handle: '', entity_type: 'community', keywords: '', priority: 2, collaboration_note: '' });
    setEditingTag(null);
    setShowForm(false);
  };

  const handleEdit = (tag) => {
    setForm({
      name: tag.name, region: tag.region, fb_handle: tag.fb_handle || '',
      ig_handle: tag.ig_handle || '', entity_type: tag.entity_type || 'community',
      keywords: (tag.keywords || []).join(', '), priority: tag.priority || 2,
      collaboration_note: tag.collaboration_note || ''
    });
    setEditingTag(tag);
    setShowForm(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      keywords: form.keywords.split(',').map(k => k.trim()).filter(Boolean),
      priority: parseInt(form.priority)
    };
    if (editingTag) {
      await fetch(`${API}/api/social/tags/${editingTag.id}`, { method: 'PUT', headers: apiHeaders(), body: JSON.stringify(payload) });
      toast.success('Tag aggiornato');
    } else {
      await fetch(`${API}/api/social/tags`, { method: 'POST', headers: apiHeaders(), body: JSON.stringify(payload) });
      toast.success('Tag creato');
    }
    resetForm();
    fetchTags();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo tag partner?')) return;
    await fetch(`${API}/api/social/tags/${id}`, { method: 'DELETE', headers: apiHeaders() });
    toast.success('Tag eliminato');
    fetchTags();
  };

  // Group by region
  const grouped = tags.reduce((acc, tag) => {
    const r = tag.region || 'altro';
    if (!acc[r]) acc[r] = [];
    acc[r].push(tag);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="tags-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Tag className="h-5 w-5 text-indigo-600" />
        <div>
          <h3 className="text-base font-semibold text-slate-800">Tag Partner & Cross-Promotion</h3>
          <p className="text-xs text-slate-500">Gestisci le pagine/community da taggare nei post. Max 1-2 tag per post (anti-spam).</p>
        </div>
        <div className="flex-1" />
        <Input placeholder="Filtra per regione..." value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className="w-40 h-8 text-sm" />
        <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-tag-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuovo Tag
        </Button>
      </div>

      {showForm && (
        <Card className="border-indigo-200 bg-indigo-50/30" data-testid="tag-form">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input placeholder="Nome (es. Comune di Cogoleto)" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="tag-name-input" />
              <Input placeholder="Regione (es. liguria)" value={form.region} onChange={e => setForm({...form, region: e.target.value})} data-testid="tag-region-input" />
              <Input placeholder="@handle Facebook" value={form.fb_handle} onChange={e => setForm({...form, fb_handle: e.target.value})} data-testid="tag-fb-input" />
              <Input placeholder="@handle Instagram" value={form.ig_handle} onChange={e => setForm({...form, ig_handle: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select className="border rounded-md px-3 py-2 text-sm bg-white" value={form.entity_type} onChange={e => setForm({...form, entity_type: e.target.value})}>
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select className="border rounded-md px-3 py-2 text-sm bg-white" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} data-testid="tag-priority-select">
                <option value={1}>Priorità 1 - Sempre (regola fissa)</option>
                <option value={2}>Priorità 2 - Quando pertinente</option>
                <option value={3}>Priorità 3 - Occasionale</option>
              </select>
              <Input placeholder="Keywords (virgola)" value={form.keywords} onChange={e => setForm({...form, keywords: e.target.value})} data-testid="tag-keywords-input" />
              <Input placeholder="Note collaborazione" value={form.collaboration_note} onChange={e => setForm({...form, collaboration_note: e.target.value})} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={resetForm}>Annulla</Button>
              <Button size="sm" onClick={handleSave} data-testid="save-tag-btn">{editingTag ? 'Aggiorna' : 'Crea'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Tag className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Nessun tag partner configurato.</p>
          <p className="text-sm mt-1">Aggiungi i tuoi partner per attivare il tagging intelligente.</p>
        </div>
      ) : (
        Object.entries(grouped).sort().map(([region, regionTags]) => (
          <Card key={region}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 capitalize">
                <MapPin className="h-4 w-4 text-indigo-600" /> {region}
                <Badge className="bg-indigo-100 text-indigo-700">{regionTags.length} tag</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {regionTags.map(tag => {
                  const eType = ENTITY_TYPES.find(t => t.value === tag.entity_type);
                  return (
                    <div key={tag.id} className="flex items-center gap-2 bg-slate-50 rounded-md p-2 group" data-testid={`tag-${tag.id}`}>
                      <div className={`h-2 w-2 rounded-full flex-shrink-0 ${tag.priority === 1 ? 'bg-red-500' : tag.priority === 2 ? 'bg-amber-500' : 'bg-gray-400'}`} />
                      <span className="text-sm font-medium text-slate-800 min-w-[140px]">{tag.name}</span>
                      <Badge className="text-[10px] bg-indigo-100 text-indigo-700">{tag.fb_handle}</Badge>
                      {tag.ig_handle && <Badge className="text-[10px] bg-pink-100 text-pink-700">{tag.ig_handle}</Badge>}
                      {eType && <Badge className={`text-[10px] ${eType.color}`}>{eType.label}</Badge>}
                      <span className="text-[10px] text-slate-400 flex-1 truncate">{(tag.keywords || []).join(', ')}</span>
                      {tag.collaboration_note && (
                        <span className="text-[10px] text-emerald-600 flex items-center gap-0.5"><Handshake className="h-3 w-3" />{tag.collaboration_note}</span>
                      )}
                      <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 flex gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 touch-manipulation" onClick={() => handleEdit(tag)}><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-500 touch-manipulation" onClick={() => handleDelete(tag.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function SocialMediaManager() {
  const [pages, setPages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [generating, setGenerating] = useState(false);
  const [filterDate, setFilterDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [filterStatus, setFilterStatus] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [allApprovedPosts, setAllApprovedPosts] = useState([]);
  const [showAllDates, setShowAllDates] = useState(false);
  // Custom Post creation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ page_id: '', topic: '', scheduled_date: '', scheduled_time: '10:00', manual_text: '', link_url: '', link_include: false, link_inspiration: false });
  const [createPhotos, setCreatePhotos] = useState([]); // [{file, preview}]
  const [coverIndex, setCoverIndex] = useState(0); // indice foto copertina
  const [createVideo, setCreateVideo] = useState(null); // {file, preview} singolo, alternativo alle foto
  const [creatingPost, setCreatingPost] = useState(false);
  const MAX_PHOTOS = 10;

  const fetchPages = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/social/pages`, { headers: apiHeaders() });
      if (r.ok) {
        const data = await r.json();
        setPages(data);
      } else {
        console.error('[SocialMedia] fetchPages failed:', r.status, r.statusText);
      }
    } catch (err) {
      console.error('[SocialMedia] fetchPages error:', err);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    let url = `${API}/api/social/posts?limit=100&_t=${Date.now()}`;
    if (!showAllDates && filterDate) url += `&date=${filterDate}`;
    if (filterStatus) url += `&status=${filterStatus}`;
    const r = await fetch(url, { headers: apiHeaders(), cache: 'no-store' });
    if (r.ok) setPosts(await r.json());
  }, [filterDate, filterStatus, showAllDates]);

  const fetchStats = useCallback(async () => {
    const r = await fetch(`${API}/api/social/stats?_t=${Date.now()}`, { headers: apiHeaders(), cache: 'no-store' });
    if (r.ok) setStats(await r.json());
  }, []);

  const fetchAllApproved = useCallback(async () => {
    const r = await fetch(`${API}/api/social/posts?status=approved&limit=200&_t=${Date.now()}`, { headers: apiHeaders(), cache: 'no-store' });
    if (r.ok) setAllApprovedPosts(await r.json());
  }, []);

  useEffect(() => { fetchPages(); fetchStats(); fetchAllApproved(); }, [fetchPages, fetchStats, fetchAllApproved]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/social/posts/generate`, {
        method: 'POST', headers: apiHeaders(), body: JSON.stringify({ target_date: filterDate })
      });
      const data = await r.json();
      if (r.ok && data.success === false) {
        // Job bloccato da generazione già in corso
        toast.warning(data.message || 'Generazione già in corso. Attendi il completamento.');
        setGenerating(false);
      } else if (r.ok && data.background) {
        toast.success('Generazione avviata in background. I post appariranno man mano...');
        // Poll for new posts every 15s
        const pollInterval = setInterval(() => {
          fetchPosts();
          fetchStats();
        }, 15000);
        // Stop polling after 10 min
        setTimeout(() => {
          clearInterval(pollInterval);
          setGenerating(false);
          fetchPosts(); fetchStats(); fetchAllApproved();
          toast.info('Generazione completata');
        }, 600000);
        // Also check job status
        if (data.job_id) {
          const checkJob = async () => {
            try {
              const jr = await fetch(`${API}/api/social/posts/generate-status/${data.job_id}`, { headers: apiHeaders() });
              const jd = await jr.json();
              if (jd.status === 'completed') {
                clearInterval(pollInterval);
                setGenerating(false);
                fetchPosts(); fetchStats(); fetchAllApproved();
                toast.success(`Generazione completata: ${jd.generated || 0} post creati, ${jd.skipped || 0} già esistenti`);
              } else if (jd.status === 'failed') {
                clearInterval(pollInterval);
                setGenerating(false);
                toast.error(`Generazione fallita: ${jd.error || 'errore sconosciuto'}`);
              }
            } catch {}
          };
          const jobPoll = setInterval(checkJob, 20000);
          setTimeout(() => clearInterval(jobPoll), 600000);
        }
      } else if (r.ok) {
        const gen = data.results?.filter(r => r.status === 'generated').length || 0;
        toast.success(`${gen} post generati per ${filterDate}`);
        fetchPosts(); fetchStats(); fetchAllApproved();
        setGenerating(false);
      } else {
        toast.error(data.detail || 'Errore');
        setGenerating(false);
      }
    } catch (e) {
      toast.error('Errore: ' + e.message);
      setGenerating(false);
    }
  };

  const refreshAll = () => { fetchPosts(); fetchStats(); fetchAllApproved(); };

  const fileToBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

  const handleCreateCustomPost = async () => {
    if (!createForm.page_id) { toast.error('Seleziona una pagina'); return; }
    const hasLinkSpunto = createForm.link_url && createForm.link_inspiration;
    if (!createForm.topic && !createForm.manual_text && !hasLinkSpunto) {
      toast.error('Inserisci un argomento, un testo manuale o un link da cui prendere spunto'); return;
    }
    setCreatingPost(true);
    try {
      const body = { ...createForm };
      // Foto multiple: ordina con la copertina per prima
      if (createPhotos.length > 0) {
        const ordered = [createPhotos[coverIndex], ...createPhotos.filter((_, i) => i !== coverIndex)];
        body.images_base64 = await Promise.all(ordered.map(p => fileToBase64(p.file)));
      }
      // Video singolo (alternativo alle foto)
      if (createVideo?.file) {
        body.video_base64 = await fileToBase64(createVideo.file);
      }
      const r = await fetch(`${API}/api/social/posts/create-custom`, {
        method: 'POST', headers: apiHeaders(), body: JSON.stringify(body)
      });
      const data = await r.json();
      if (data.success) {
        let msg = 'Post creato!';
        if (data.images_count > 1) msg += ` (${data.images_count} foto)`;
        else if (data.has_video) msg += ' (con video)';
        if (createForm.link_inspiration && data.link_read === false && data.link_note) {
          toast.warning(`Link non leggibile: ${data.link_note}. Ho usato comunque l'URL come riferimento.`);
        }
        toast.success(msg);
        setShowCreateModal(false);
        setCreateForm({ page_id: '', topic: '', scheduled_date: '', scheduled_time: '10:00', manual_text: '', link_url: '', link_include: false, link_inspiration: false });
        createPhotos.forEach(p => p.preview && URL.revokeObjectURL(p.preview));
        if (createVideo?.preview) URL.revokeObjectURL(createVideo.preview);
        setCreatePhotos([]); setCoverIndex(0); setCreateVideo(null);
        refreshAll();
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore: ' + e.message); }
    setCreatingPost(false);
  };

  const pendingCount = posts.filter(p => p.status === 'pending_approval').length;
  
  // Filtro testuale per pagina (case-insensitive, ricerca su name + geo_area).
  // Esempio: scrivi "piemonte" e vedi solo i post della pagina Piemonte in ogni tab.
  const matchesPageSearch = (post) => {
    if (!pageSearch.trim()) return true;
    const q = pageSearch.trim().toLowerCase();
    const name = (post.page_name || '').toLowerCase();
    // Cerca anche tramite page_id → page object (geo_area)
    const page = pages.find(p => p.id === post.page_id);
    const geo = (page?.geo_area || '').toLowerCase();
    const platform = (page?.platform || '').toLowerCase();
    return name.includes(q) || geo.includes(q) || platform.includes(q);
  };
  
  const filteredPosts = posts.filter(matchesPageSearch);
  const filteredAllApproved = allApprovedPosts.filter(matchesPageSearch);
  const approvedPosts = filteredPosts.filter(p => p.status === 'approved');
  const publishedPosts = filteredPosts.filter(p => p.status === 'published');
  const rejectedPosts = filteredPosts.filter(p => p.status === 'rejected');

  return (
    <div className="space-y-5" data-testid="social-media-manager">
      {/* Stats grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: 'Pagine', value: stats.total_pages || 0, color: 'text-slate-800' },
          { label: 'Da approvare', value: stats.pending_approval || 0, color: 'text-amber-600' },
          { label: 'Approvati', value: stats.approved || 0, color: 'text-blue-600' },
          { label: 'Domani', value: stats.tomorrow_scheduled || 0, color: 'text-cyan-600' },
          { label: 'Pubblicati', value: stats.published || 0, color: 'text-emerald-600' },
          { label: 'Correzioni AI', value: stats.total_feedback || 0, color: 'text-violet-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="py-2.5 text-center">
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-[10px] text-slate-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="approval" className="w-full">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList data-testid="social-tabs" className="inline-flex w-auto min-w-full md:grid md:w-full md:grid-cols-8 gap-1">
            <TabsTrigger value="approval" data-testid="tab-approval" className="min-w-[100px] whitespace-nowrap px-3 py-2.5">
              Approvazione {pendingCount > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px] h-5 min-w-5">{pendingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts" className="min-w-[90px] whitespace-nowrap px-3 py-2.5">Tutti i Post</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="min-w-[90px] whitespace-nowrap px-3 py-2.5">
              <LineChart className="h-3.5 w-3.5 mr-1" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages" className="min-w-[80px] whitespace-nowrap px-3 py-2.5">Pagine ({pages.length})</TabsTrigger>
            <TabsTrigger value="tags" data-testid="tab-tags" className="min-w-[80px] whitespace-nowrap px-3 py-2.5">
              <Tag className="h-3.5 w-3.5 mr-1" /> Tagging
            </TabsTrigger>
            <TabsTrigger value="learning" data-testid="tab-learning" className="min-w-[100px] whitespace-nowrap px-3 py-2.5">
              <Brain className="h-3.5 w-3.5 mr-1" /> AI Learning
            </TabsTrigger>
            <TabsTrigger value="ai-command" data-testid="tab-ai-command" className="min-w-[130px] whitespace-nowrap px-3 py-2.5">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Centro Comando AI
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar" className="min-w-[120px] whitespace-nowrap px-3 py-2.5">
              <CalendarDays className="h-3.5 w-3.5 mr-1" /> Calendario
            </TabsTrigger>
          </TabsList>
        </div>

        {/* APPROVAL TAB */}
        <TabsContent value="approval" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setShowAllDates(false); }} className="w-44" data-testid="filter-date" />
            <Button variant={showAllDates ? "default" : "outline"} size="sm" 
              onClick={() => setShowAllDates(!showAllDates)} data-testid="show-all-dates-btn"
              className={showAllDates ? "bg-blue-600 hover:bg-blue-700" : ""}>
              <Eye className="h-4 w-4 mr-1" /> {showAllDates ? 'Tutte le date' : 'Tutte le date'}
            </Button>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={pageSearch}
                onChange={e => setPageSearch(e.target.value)}
                placeholder="Cerca pagina (es. piemonte, calabria…)"
                className="w-64 pl-8"
                data-testid="approval-page-search"
              />
              {pageSearch && (
                <button
                  type="button"
                  onClick={() => setPageSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="approval-page-search-clear"
                  aria-label="Pulisci ricerca"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Button onClick={handleGenerate} disabled={generating || pages.length === 0}
              className="bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="generate-posts-btn">
              {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generazione...</>
                : <><RefreshCw className="h-4 w-4 mr-2" /> Genera post per {filterDate}</>}
            </Button>
            <Button onClick={() => setShowCreateModal(true)} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="create-custom-post-btn">
              <PenTool className="h-4 w-4 mr-2" /> Crea Post
            </Button>
            {pages.length === 0 && (
              <span className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Crea prima almeno una pagina</span>
            )}
          </div>
          {/* Banner: approved posts from other dates waiting to be published */}
          {filteredAllApproved.length > 0 && !showAllDates && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => setShowAllDates(true)} data-testid="approved-posts-banner">
              <Send className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-800 font-medium">
                Hai {filteredAllApproved.length} post approvati da pubblicare
                {filteredAllApproved.some(p => p.scheduled_date < new Date().toISOString().split('T')[0]) && 
                  ` (inclusi post di date passate)`
                }
                {pageSearch && ` per "${pageSearch}"`}
                . Clicca per vederli tutti.
              </span>
            </div>
          )}
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="cron-info-banner">
            <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="text-xs text-blue-700">
              <strong>Generazione automatica:</strong> Ogni giorno alle 7:00 l'AI genera post per il giorno successivo (G+1) per tutte le pagine attive. 
              I post vengono creati con stato "Da approvare" — puoi anche generarli manualmente col pulsante sopra.
            </span>
          </div>
          {/* Show approved posts section when viewing all dates */}
          {showAllDates && filteredAllApproved.length > 0 && (
            <ApprovedPostsSection posts={filteredAllApproved} pages={pages} onRefresh={refreshAll} />
          )}
          <ApprovalTab posts={filteredPosts} pages={pages} onRefresh={refreshAll} />
        </TabsContent>

        {/* ALL POSTS TAB */}
        <TabsContent value="posts" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setShowAllDates(false); }} className="w-44" />
            <Button variant={showAllDates ? "default" : "outline"} size="sm" 
              onClick={() => setShowAllDates(!showAllDates)} data-testid="show-all-dates-posts-btn"
              className={showAllDates ? "bg-blue-600 hover:bg-blue-700" : ""}>
              <Eye className="h-4 w-4 mr-1" /> {showAllDates ? 'Tutte le date' : 'Tutte le date'}
            </Button>
            <select className="border rounded-md px-3 py-2 text-sm bg-white" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="filter-status">
              <option value="">Tutti gli stati</option>
              <option value="pending_approval">Da approvare</option>
              <option value="approved">Approvati</option>
              <option value="published">Pubblicati</option>
              <option value="rejected">Rifiutati</option>
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={pageSearch}
                onChange={e => setPageSearch(e.target.value)}
                placeholder="Cerca pagina (es. piemonte, calabria…)"
                className="w-64 pl-8"
                data-testid="posts-page-search"
              />
              {pageSearch && (
                <button
                  type="button"
                  onClick={() => setPageSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="posts-page-search-clear"
                  aria-label="Pulisci ricerca"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <span className="text-xs text-slate-500">
              {filteredPosts.length} post {pageSearch ? `per "${pageSearch}"` : 'trovati'}{showAllDates ? ' (tutte le date)' : ''}
            </span>
          </div>

          {(showAllDates ? filteredAllApproved : approvedPosts).length > 0 && (
            <ApprovedPostsSection posts={showAllDates ? filteredAllApproved : approvedPosts} pages={pages} onRefresh={refreshAll} />
          )}

          {publishedPosts.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1.5"><Send className="h-4 w-4" /> Pubblicati ({publishedPosts.length})</h4>
              <div className="space-y-2">{publishedPosts.map(p => <PostCard key={p.id} post={p} pages={pages} onRefresh={refreshAll} />)}</div>
            </div>
          )}

          {rejectedPosts.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-rose-700 mb-2 flex items-center gap-1.5"><X className="h-4 w-4" /> Rifiutati ({rejectedPosts.length})</h4>
              <div className="space-y-2">{rejectedPosts.map(p => <PostCard key={p.id} post={p} pages={pages} onRefresh={refreshAll} />)}</div>
            </div>
          )}

          {filteredPosts.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Share2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-base">
                {pageSearch
                  ? `Nessun post per "${pageSearch}"${showAllDates ? '' : ' in questa data'}`
                  : 'Nessun post per questa data'}
              </p>
            </div>
          )}
        </TabsContent>

        {/* PAGES TAB */}
        <TabsContent value="pages">
          <PagesTab pages={pages} onRefresh={fetchPages} />
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics">
          <AnalyticsTab pages={pages} />
        </TabsContent>

        {/* TAGS TAB */}
        <TabsContent value="tags">
          <TagsTab />
        </TabsContent>

        {/* LEARNING TAB */}
        <TabsContent value="learning">
          <div className="space-y-6">
            <PerformanceInsightsPanel pages={pages} />
            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-700 mb-3">Regole Manuali</h3>
              <LearningTab pages={pages} />
            </div>
          </div>
        </TabsContent>

        {/* AI COMMAND CENTER TAB */}
        <TabsContent value="ai-command">
          <AICommandCenter pages={pages} token={getToken()} />
        </TabsContent>

        {/* PUBLISHED CALENDAR TAB */}
        <TabsContent value="calendar">
          <PublishedCalendarTab />
        </TabsContent>
      </Tabs>

      {/* ═══ CREATE CUSTOM POST MODAL ═══ */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><PenTool className="h-5 w-5 text-emerald-600" /> Crea Post Personalizzato</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Page selection */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Pagina *</label>
                <select value={createForm.page_id} onChange={(e) => setCreateForm(f => ({ ...f, page_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="create-page-select">
                  <option value="">Seleziona pagina...</option>
                  {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  <Sparkles className="h-3.5 w-3.5 inline mr-1 text-amber-500" />
                  Argomento per l'AI *
                </label>
                <textarea value={createForm.topic} onChange={(e) => setCreateForm(f => ({ ...f, topic: e.target.value }))}
                  placeholder="Es: Pasqua in Liguria, Weekend romantico a Finale Ligure, Cosa vedere a Genova in 3 giorni..."
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[80px] resize-y" data-testid="create-topic" />
                <p className="text-[10px] text-slate-400 mt-0.5">L'AI svilupperà questo argomento nel post. Lascia vuoto se scrivi il testo manualmente.</p>
              </div>

              {/* Manual text (optional) */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Testo manuale (opzionale)</label>
                <textarea value={createForm.manual_text} onChange={(e) => setCreateForm(f => ({ ...f, manual_text: e.target.value }))}
                  placeholder="Scrivi il testo del post direttamente (bypassa AI)..."
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[60px] resize-y" data-testid="create-manual-text" />
              </div>

              {/* Media upload: foto multiple (con scelta copertina) o video singolo */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Foto (multiple) o Video</label>
                <div className="flex gap-2">
                  <label className={`cursor-pointer flex-1 ${createVideo ? 'opacity-40 pointer-events-none' : ''}`} data-testid="create-upload-image">
                    <input type="file" className="hidden" accept="image/*" multiple disabled={!!createVideo} onChange={(e) => {
                      const files = Array.from(e.target.files || []); if (!files.length) return;
                      setCreatePhotos(prev => {
                        const next = [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))].slice(0, MAX_PHOTOS);
                        return next;
                      });
                      e.target.value = '';
                    }} />
                    <div className="flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                      <ImageIcon className="h-4 w-4" /> Carica Foto
                    </div>
                  </label>
                  <label className={`cursor-pointer flex-1 ${createPhotos.length ? 'opacity-40 pointer-events-none' : ''}`} data-testid="create-upload-video">
                    <input type="file" className="hidden" accept="video/mp4,video/quicktime,video/mov,video/*" disabled={createPhotos.length > 0} onChange={(e) => {
                      const file = e.target.files[0]; if (!file) return;
                      setCreateVideo({ file, preview: URL.createObjectURL(file) });
                      e.target.value = '';
                    }} />
                    <div className="flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                      <Video className="h-4 w-4" /> Carica Video
                    </div>
                  </label>
                </div>
                {createPhotos.length > 0 && (
                  <>
                    <p className="text-[10px] text-slate-400 mt-1.5">Clicca una foto per impostarla come <b>copertina</b>. Le altre diventano carosello. ({createPhotos.length}/{MAX_PHOTOS})</p>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="create-photos-grid">
                      {createPhotos.map((p, i) => (
                        <div key={i} className={`relative cursor-pointer rounded-lg overflow-hidden border-2 ${i === coverIndex ? 'border-emerald-500' : 'border-transparent'}`}
                          onClick={() => setCoverIndex(i)} data-testid={`create-photo-${i}`}>
                          <img src={p.preview} alt="" className="h-24 w-24 object-cover" />
                          {i === coverIndex && (
                            <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold">Copertina</span>
                          )}
                          <button type="button" onClick={(e) => { e.stopPropagation(); URL.revokeObjectURL(p.preview);
                              setCreatePhotos(prev => prev.filter((_, idx) => idx !== i));
                              setCoverIndex(ci => (i === ci ? 0 : (i < ci ? ci - 1 : ci)));
                            }}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-photo-${i}`}><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {createVideo?.preview && (
                  <div className="mt-2 relative inline-block">
                    <video src={createVideo.preview} className="h-32 rounded-lg" controls />
                    <button type="button" onClick={() => { URL.revokeObjectURL(createVideo.preview); setCreateVideo(null); }}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid="remove-video"><X className="h-3 w-3" /></button>
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded">Video</span>
                  </div>
                )}
              </div>

              {/* Link di riferimento */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  <LinkIcon className="h-3.5 w-3.5 inline mr-1 text-blue-500" />
                  Link di riferimento (opzionale)
                </label>
                <input type="url" value={createForm.link_url} onChange={(e) => setCreateForm(f => ({ ...f, link_url: e.target.value }))}
                  placeholder="https://..." className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="create-link-url" />
                <div className="mt-2 space-y-1.5">
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={createForm.link_include} onChange={(e) => setCreateForm(f => ({ ...f, link_include: e.target.checked }))}
                      data-testid="create-link-include" className="rounded" />
                    Includi il link nel testo del post
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={createForm.link_inspiration} onChange={(e) => setCreateForm(f => ({ ...f, link_inspiration: e.target.checked }))}
                      data-testid="create-link-inspiration" className="rounded" />
                    Usa il link come spunto per l'AI (legge il contenuto della pagina)
                  </label>
                </div>
              </div>


              {/* Schedule */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Data pubblicazione</label>
                  <input type="date" value={createForm.scheduled_date} onChange={(e) => setCreateForm(f => ({ ...f, scheduled_date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="create-date" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ora</label>
                  <input type="time" value={createForm.scheduled_time} onChange={(e) => setCreateForm(f => ({ ...f, scheduled_time: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="create-time" />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annulla</Button>
              <Button onClick={handleCreateCustomPost} disabled={creatingPost} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-post-submit">
                {creatingPost ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generazione AI...</>
                  : <><Sparkles className="h-4 w-4 mr-2" /> Crea Post</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
